"use client";
import React from "react";
import {
  motion,
  useMotionValue,
} from "motion/react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useDocumentVisible } from '@/hooks/useDocumentVisible';
import { useElementInView } from '@/hooks/useElementInView';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export function Button({
  borderRadius = "1.75rem",
  children,
  as: Component = "button",
  containerClassName,
  borderClassName,
  duration,
  className,
  ...otherProps
}: {
  borderRadius?: string;
  children: React.ReactNode;
  as?: any;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  className?: string;
  [key: string]: any;
}) {
  return (
    <Component
      className={cn(
        "relative h-16 w-40 overflow-hidden bg-transparent p-[1px] text-xl",
        containerClassName,
      )}
      style={{
        borderRadius: borderRadius,
      }}
      {...otherProps}
    >
      <div
        className="absolute inset-0"
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      >
        <MovingBorder duration={duration} rx="30%" ry="30%">
          <div
            className={cn(
              "h-20 w-20 bg-[radial-gradient(#ffffff_40%,transparent_60%)] opacity-[0.8]",
              borderClassName,
            )}
          />
        </MovingBorder>
      </div>

      <div
        className={cn(
          "relative flex h-full w-full items-center justify-center border border-slate-800 bg-slate-900/[0.8] text-sm text-white antialiased backdrop-blur-xl",
          className,
        )}
        style={{
          borderRadius: `calc(${borderRadius} * 0.96)`,
        }}
      >
        {children}
      </div>
    </Component>
  );
}

export const MovingBorder = ({
  children,
  duration = 3000,
  rx,
  ry,
  borderOffset,
  ...otherProps
}: {
  children: React.ReactNode;
  duration?: number;
  rx?: string;
  ry?: string;
  borderOffset?: number;
  [key: string]: any;
}) => {
  const pathRef = useRef<SVGRectElement | null>(null);
  const dotRef = useRef<HTMLSpanElement | null>(null);
  const autoOffset = useRef(0);
  const pathLength = useRef<number | null>(null);
  const elapsedTime = useRef(0);
  const { ref: svgRef, inView } = useElementInView<SVGSVGElement>('200px');
  const documentVisible = useDocumentVisible();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const active = inView && documentVisible && !reducedMotion;
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const angle = useMotionValue(0);

  useLayoutEffect(() => {
    if (borderOffset !== undefined) {
      return;
    }

    const height = dotRef.current?.offsetHeight ?? 0;
    if (!height) {
      return;
    }

    autoOffset.current = Math.max(0, height / 2 - 1);
  }, [borderOffset]);

  useLayoutEffect(() => {
    pathLength.current = null;
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => { pathLength.current = null; });
    observer.observe(svg);
    return () => observer.disconnect();
  }, [rx, ry, svgRef, otherProps.viewBox]);

  useEffect(() => {
    if (!active) return;
    let frameId: number;
    let previousTime: number | undefined;
    let cancelled = false;

    const update = (time: number) => {
      if (cancelled) return;
      if (previousTime !== undefined) elapsedTime.current += time - previousTime;
      previousTime = time;
      const path = pathRef.current;
      if (path) {
        // Older browsers without ResizeObserver still remeasure a responsive path.
        const length = pathLength.current ?? path.getTotalLength();
        if (typeof ResizeObserver !== 'undefined') pathLength.current = length;
        if (length) {
          const pxPerMillisecond = length / duration;
          const progress = (elapsedTime.current * pxPerMillisecond) % length;
          const current = path.getPointAtLength(progress);
          const next = path.getPointAtLength((progress + 1) % length);
          const tangent = Math.atan2(next.y - current.y, next.x - current.x);
          const offset = borderOffset ?? autoOffset.current;
          const normal = tangent + Math.PI / 2;

          x.set(current.x + Math.cos(normal) * offset);
          y.set(current.y + Math.sin(normal) * offset);
          angle.set((tangent * 180) / Math.PI);
        }
      }
      frameId = requestAnimationFrame(update);
    };
    frameId = requestAnimationFrame(update);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [active, angle, borderOffset, duration, x, y]);

  return (
    <>
      <svg
        ref={svgRef}
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className="absolute h-full w-full"
        width="100%"
        height="100%"
        {...otherProps}
      >
        <rect
          fill="none"
          width="100%"
          height="100%"
          rx={rx}
          ry={ry}
          ref={pathRef}
        />
      </svg>
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          x,
          y,
        }}
        transformTemplate={(_, generatedTransform) =>
          `${generatedTransform} translate(-50%, -50%)`
        }
      >
        <motion.div
          style={{
            display: "inline-block",
            rotate: angle,
            transformOrigin: "50% 50%",
          }}
        >
          <span ref={dotRef} style={{ display: "inline-block" }}>
            {children}
          </span>
        </motion.div>
      </motion.div>
    </>
  );
};
